import { LAUNCH_DARKLY_KEY } from './Environment';
import type { FeatureFlagName } from './FeatureFlagNames';
import { type AllFeatureFlags, ALL_FEATURE_FLAGS_SCHEMA, type HeaderWidget } from './FeatureFlagSchema';
import {
	initialize,
	type LDFlagValue,
	type LDClient,
	type LDContext,
	type LDMultiKindContext,
	type LDContextCommon,
} from 'launchdarkly-js-client-sdk';
import { makeAutoObservable } from 'mobx';

const LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY = 'user';
const LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY = 'patient';
const LAUNCHDARKLY_MULTI_ACCOUNT_SEGMENT_KEY = 'account';
const LAUNCHDARKLY_MULTI_GROUP_PRACTICE_SEGMENT_KEY = 'group_practice';

const _LAUNCHDARKLY_CONTEXT_KINDS = [
	LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY,
	LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY,
	LAUNCHDARKLY_MULTI_ACCOUNT_SEGMENT_KEY,
	LAUNCHDARKLY_MULTI_GROUP_PRACTICE_SEGMENT_KEY,
] as const;
type LaunchDarklyContextKind = (typeof _LAUNCHDARKLY_CONTEXT_KINDS)[number];

type UserKindContext = LDContextCommon & Required<Pick<LDContextCommon, 'key'>>;
type PatientKindContext = LDContextCommon & Required<Pick<LDContextCommon, 'key'>>;

function withKindContext<K extends string>(
	contextName: K,
	context: LDContextCommon,
): { readonly [P in K]: LDContextCommon };

function withKindContext(contextName: string, context: LDContextCommon) {
	return {
		[contextName]: context,
	} as const;
}

function withUserKindContext(context: UserKindContext) {
	return withKindContext(LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY, context);
}

type OnFlagsInitializedCallbacks = (initializedClient: LDClient) => void;

interface InitParams {
	readonly id: string;
	readonly email: string;
	readonly custom: LDContext['custom'];
}

class FeatureFlag {
	private onFlagsInitializedCallbacks: ReadonlyArray<OnFlagsInitializedCallbacks> = [];
	client: LDClient | null = null;
	flagsAreInitialized = false;

	private contexts: Partial<Record<LaunchDarklyContextKind, LDContextCommon>> = {};
	private maybeAllFlags: AllFeatureFlags | null = null;

	private constructor() {
		makeAutoObservable(this);
	}

	static readonly create = (): FeatureFlag => new FeatureFlag();

	private readonly setOnFlagsInitializedCallbacks = (callbacks: ReadonlyArray<OnFlagsInitializedCallbacks>): void => {
		this.onFlagsInitializedCallbacks = callbacks;
	};

	private readonly resetOnFlagsInitializedCallbacks = (): void => {
		this.setOnFlagsInitializedCallbacks([]);
	};

	private readonly addOnFlagsInitializedCallback = (callback: OnFlagsInitializedCallbacks): void => {
		this.setOnFlagsInitializedCallbacks([...this.onFlagsInitializedCallbacks, callback]);
	};

	private readonly setContexts = (additionalContexts: typeof this.contexts): void => {
		this.contexts = additionalContexts;
	};

	private readonly setAllFlags = (flags: unknown): void => {
		const parsedFlags = ALL_FEATURE_FLAGS_SCHEMA.safeParse(flags);

		if (!parsedFlags.success) {
			throw new Error(`Failed to parse feature flags: ${parsedFlags.error.message}`);
		}

		this.maybeAllFlags = parsedFlags.data;
	};

	private readonly setClient = (client: typeof this.client): void => {
		this.client = client;
	};

	private readonly setFlagsAreInitialized = (value: typeof this.flagsAreInitialized): void => {
		this.flagsAreInitialized = value;
	};

	get initializedClient(): LDClient {
		if (!this.client) {
			throw new Error('FeatureFlag client is not initialized');
		}

		return this.client;
	}

	private readonly setContextLocally = (
		contextName: LaunchDarklyContextKind,
		context: LDContextCommon | undefined,
	): void => {
		const { [contextName]: _currentContext, ...restContexts } = this.contexts;

		this.setContexts({
			...restContexts,
			...(context ? { [contextName]: context } : {}),
		});
	};

	private readonly extendContextLocally = (
		contextName: LaunchDarklyContextKind,
		partialNewContext: LDContextCommon,
	): void => {
		const currentContext = this.contexts[contextName] ?? {};
		const newContext = {
			...currentContext,
			...partialNewContext,
		};

		this.setContextLocally(contextName, newContext);
	};

	readonly updateContextRemotely = (newContext: Omit<LDMultiKindContext, 'kind'>): void => {
		const { initializedClient } = this;

		const contextWithKind = {
			kind: 'multi',
			...newContext,
		} as const satisfies LDMultiKindContext;

		initializedClient.identify(contextWithKind);
	};

	readonly updateContextRemotelyFromLocal = (): void => {
		const { contexts } = this;
		this.updateContextRemotely(contexts);
	};

	private readonly setContextLocallyAndSyncRemotely = (
		contextName: LaunchDarklyContextKind,
		context: LDContextCommon | undefined,
	): void => {
		this.setContextLocally(contextName, context);
		this.updateContextRemotelyFromLocal();
	};

	private readonly extendContextLocallyAndSyncRemotely = (
		contextName: LaunchDarklyContextKind,
		partialNewContext: LDContextCommon,
	): void => {
		this.extendContextLocally(contextName, partialNewContext);
		this.updateContextRemotelyFromLocal();
	};

	public readonly setPatientContext = (patientContext: PatientKindContext): void => {
		this.setContextLocallyAndSyncRemotely(LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY, patientContext);
	};

	public readonly resetPatientContext = (): void => {
		this.setContextLocallyAndSyncRemotely(LAUNCHDARKLY_MULTI_PATIENT_SEGMENT_KEY, undefined);
	};

	public readonly extendUserContext = (partialNewUserContext: Omit<UserKindContext, 'key'>): void => {
		this.extendContextLocallyAndSyncRemotely(LAUNCHDARKLY_MULTI_USER_SEGMENT_KEY, partialNewUserContext);
	};

	readonly init = async ({ id, email, custom }: InitParams): Promise<void> => {
		return new Promise((resolve, reject) => {
			const { setAllFlags, setContextLocally, setClient, setFlagsAreInitialized, resetOnFlagsInitializedCallbacks } =
				this;

			const customClone: LDContext['custom'] = { ...custom };

			const { first_name: _firstName, last_name: _lastName, ...rest } = customClone;

			const context = {
				kind: 'multi',
				...withUserKindContext({
					key: id,
					email,
					...rest,
				}),
			} as const satisfies LDMultiKindContext;

			setContextLocally('user', context.user);

			const client = initialize(LAUNCH_DARKLY_KEY, context, {
				bootstrap: 'localStorage',
				useReport: true,
			});

			setClient(client);

			function updateAllFlags(): void {
				setAllFlags(client.allFlags());
			}

			client.on('initialized', (): void => {
				setFlagsAreInitialized(true);

				for (const callback of this.onFlagsInitializedCallbacks) {
					callback(client);
				}
				resetOnFlagsInitializedCallbacks();

				updateAllFlags();

				resolve();
			});

			client.on('failed', (error: unknown) => {
				reject(new Error('FeatureFlag client failed to initialize', { cause: error }));
			});

			client.on('change', updateAllFlags);
		});
	};

	readonly onFlagsInitialized = (callback: OnFlagsInitializedCallbacks): void => {
		const { flagsAreInitialized, client } = this;

		if (flagsAreInitialized && client) {
			callback(client);
		} else {
			this.addOnFlagsInitializedCallback(callback);
		}
	};

	readonly getFeatureValue = <T extends LDFlagValue>(featureName: FeatureFlagName, defaultValue: T): T => {
		const { initializedClient } = this;
		return initializedClient.variation(featureName, defaultValue);
	};

	get allFlags(): AllFeatureFlags {
		if (!this.maybeAllFlags) {
			throw new Error('Feature flags are not initialized');
		}

		return this.maybeAllFlags;
	}

	get headerWidgets(): ReadonlyArray<HeaderWidget> {
		const key = 'configuration_CoreServices_PatientPortraitHeaderWidgets' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? [];
	}

	get showFeedbackButton(): boolean {
		const key = 'ShowFeedbackButton' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get coBrandingLogoUrl(): string {
		const key = 'configuration_CoreServices_CoBrandingLogo' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? '';
	}

	get shouldShowRafBar(): boolean {
		const key = 'configuration_DxCapture_Show_Raf' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? true;
	}

	get portraitWithoutSidActionsEnabled(): boolean {
		const key = 'configuration_DxCapture_portrait_without_sid_actions' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}

	get areActionsDisabled(): boolean {
		const key = 'configuration_DxCapture_disable_actions' as const satisfies keyof AllFeatureFlags;
		return this.maybeAllFlags?.[key] ?? false;
	}
}

let maybeFeatureFlag: FeatureFlag | null = null;

export function getOrCreateFeatureFlag(): FeatureFlag {
	maybeFeatureFlag ??= FeatureFlag.create();

	return maybeFeatureFlag;
}

export async function getOrCreateInitializedFeatureFlag(initParams: InitParams): Promise<FeatureFlag> {
	const featureFlags = getOrCreateFeatureFlag();

	await featureFlags.init(initParams);

	return featureFlags;
}
